import { composeWrappers } from "@redotech/react-util/component";
import { HidProvider } from "@redotech/react-util/hid";
import { allCatalogs } from "@redotech/redo-i18n-catalogs";
import { initI18n } from "@redotech/redo-i18n/i18n";
import { NotificationProvider } from "@redotech/redo-web/notification-provider";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { CustomSnackbarProvider } from "@redotech/redo-web/snackbar";
import { ThemeProvider } from "@redotech/redo-web/theme-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactNode, useContext, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "./components/ui/onboarding-dark-bridge.css";
import { AmplitudeProvider } from "./core/amplitude-provider";
import { getMerchantServerUrl } from "./core/auth";
import { AuthProvider, useAuth } from "./core/auth-provider";
import { prefetchMerchantInfo } from "./hooks/use-merchant-info";
import {
  useIdentifyMerchant,
  useTrackAuthHandoff,
} from "./hooks/use-onboarding-tracking";
import { prefetchProductProgress } from "./hooks/use-product-progress";
import { routes } from "./route";
import {
  OnboardingV2RpcClientContext,
  OnboardingV2RpcClientProvider,
} from "./rpc-client";
import "./styles.css";

initI18n(allCatalogs);

const router = createBrowserRouter(routes);

function RpcProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  return (
    <OnboardingV2RpcClientProvider
      authToken={auth?.token}
      baseUrl={getMerchantServerUrl()}
    >
      <PrefetchOnboardingBootstrap />
      {children}
    </OnboardingV2RpcClientProvider>
  );
}

function PrefetchOnboardingBootstrap() {
  const rpcClient = useContext(OnboardingV2RpcClientContext);
  useEffect(() => {
    prefetchMerchantInfo(rpcClient);
    prefetchProductProgress(rpcClient);
  }, [rpcClient]);
  return null;
}

function MerchantIdentifier() {
  useIdentifyMerchant();
  useTrackAuthHandoff();
  return null;
}

const OnboardingApp = () => {
  return composeWrappers(
    (inner) => <AuthProvider>{inner}</AuthProvider>,
    (inner) => <AmplitudeProvider>{inner}</AmplitudeProvider>,
    (inner) => <RpcProvider>{inner}</RpcProvider>,
    (inner) => (
      <QueryClientProvider client={reactQueryClient}>
        {inner}
      </QueryClientProvider>
    ),
    (inner) => <HidProvider>{inner}</HidProvider>,
    (inner) => <ThemeProvider>{inner}</ThemeProvider>,
    (inner) => <NotificationProvider>{inner}</NotificationProvider>,
    (inner) => <CustomSnackbarProvider>{inner}</CustomSnackbarProvider>,
  )(
    <>
      <MerchantIdentifier />
      <RouterProvider router={router} />
    </>,
  );
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(document.getElementById("root")!);
root.render(<OnboardingApp />);
